import { formatPoints } from "@iec-id/shared";
import type { OnboardingState } from "@iec-id/shared/contracts/profile";
import { Button } from "@iec-id/ui";
import { useState } from "react";
import { RewardCallout } from "@/components/shared/reward-callout";
import { type AuthSearch, resolveSignedInDestination, safeExternalUrl } from "@/lib/auth-flow";
import { useLocale } from "@/lib/locale";
import { api } from "@/lib/trpc";
import { useOnboardingT } from "../onboarding-i18n";
import { returnServiceOf } from "../return-service";
import { ServiceMark, serviceMarkSrc } from "../service-mark";
import { StepTitle } from "../step-title";

/**
 * Шаг 6 — финал (макет 4b-5).
 *
 * Бонус начисляет сервер и только он (инвариант №9): экран показывает число из
 * контракта и не считает ничего сам. Повторное нажатие безопасно —
 * `completeOnboarding` идемпотентен и вторым вызовом начисляет ноль.
 *
 * ── ДВА СОСТОЯНИЯ ПЛАШКИ ───────────────────────────────────────────────────
 *
 * С 23.08.2026 приветственные Баллы даёт не сам факт финала, а выполненная
 * задача «включить уведомления и рассылки» (решение владельца). Значит экран
 * обязан говорить правду о ДВУХ разных исходах: «начислено» и «ждут вас».
 * Плашка, обещающая начисленное там, где начисления не было, — ровно та ложь,
 * которую мы вычищаем из ленты уведомлений.
 *
 * Состояние читается с сервера (`profile.welcomeReward`), а не выводится из
 * того, что человек нажал на прошлом экране: подписка на push принадлежит
 * домену и могла быть сделана раньше, на другом устройстве.
 */
export function DoneStep({
	state,
	search,
	onCompleted,
}: {
	state: OnboardingState;
	search: AuthSearch;
	/**
	 * Куда человек уйдёт после мастера. Уход выполняет НЕ этот шаг: между ним и
	 * сервисом стоят слайды про единый профиль (Д13), и решение «показывать их
	 * или нет» принимает мастер целиком, а не его последний экран.
	 */
	onCompleted: (destination: string) => void;
}) {
	const t = useOnboardingT();
	const locale = useLocale();
	const [failure, setFailure] = useState<string | null>(null);

	const service = returnServiceOf(state, search);
	const reward = api.profile.welcomeReward.useQuery(undefined, { retry: false });
	// Задача выполнена, когда включены уведомления И приняты все согласия.
	// Уже начисленное — тоже «выполнена»: повторно показывать её незачем.
	const earned =
		reward.data?.alreadyRewarded === true ||
		(reward.data?.pushEnabled === true && reward.data.consentsAccepted);

	// Что именно осталось — называем прямо. Общее «включите уведомления»
	// человеку, который их только что включил и не поставил галочку согласия,
	// читается как поломка: он сделал ровно то, о чём его просят, и Баллов нет.
	const missing: "both" | "consent" | "push" =
		reward.data?.pushEnabled === true
			? "consent"
			: reward.data?.consentsAccepted === true
				? "push"
				: "both";

	const complete = api.profile.completeOnboarding.useMutation({
		onSuccess: async (result) => {
			// `return_to` сильнее всего: сервер о том, что мы прервали человека на
			// `/consent`, не знает — он видит только сервис-инициатор.
			const interrupted = safeExternalUrl(search.return_to);
			if (interrupted) {
				onCompleted(interrupted);
				return;
			}
			// Мастер — часть входа, а не отдельный поход к нам: человек попал сюда
			// из сервиса и обязан вернуться туда С КОДОМ. Пока здесь стоял
			// `fallbackDestination`, новичок приезжал в сервис гостем.
			onCompleted(await resolveSignedInDestination(search, result.redirectTo));
		},
		onError: () => setFailure(t("done.failed")),
	});

	const name = state.draft.firstName ?? state.draft.nickname ?? "";

	return (
		<>
			<StepTitle>{t("done.title", { name })}</StepTitle>

			{earned ? (
				<RewardCallout
					title={t("done.rewardPoints", {
						count: state.rewardPoints,
						points: formatPoints(state.rewardPoints, locale),
					})}
					caption={t("done.rewardCaption")}
				/>
			) : (
				<RewardCallout
					title={t("done.rewardWaiting", {
						count: state.rewardPoints,
						points: formatPoints(state.rewardPoints, locale),
					})}
					caption={t(`done.rewardWaitingCaption.${missing}` as const)}
					tone="lead"
				/>
			)}

			{failure ? (
				<p role="alert" className="t-body text-danger">
					{failure}
				</p>
			) : null}

			<Button
				// Якорь для браузерных сценариев: подпись зависит и от локали, и от
				// того, из какого сервиса пришёл человек, — селектора по тексту тут
				// быть не может.
				data-testid="onboarding-finish"
				variant="primary"
				fullWidth
				// Знак сервиса слева от подписи: человек возвращается туда, откуда
				// пришёл, и кнопка обязана выглядеть как дверь именно туда.
				{...(service && serviceMarkSrc(service.id)
					? { iconLeft: <ServiceMark serviceId={service.id} /> }
					: {})}
				state={complete.isPending ? "loading" : "default"}
				onClick={() => {
					setFailure(null);
					complete.mutate(service ? { serviceId: service.id } : {});
				}}
			>
				{service ? t("done.goToService", { service: service.name }) : t("done.openIecId")}
			</Button>
		</>
	);
}
